import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button } from "@fawkes/ui";

import "./schermbouwer.css";

/**
 * Het beheerscherm van de schermbouwer.
 *
 * <p>Drie vragen die bij de beheerder en de productowner horen en niet bij de ontwerper:
 *
 * <ul>
 *   <li><b>Wat mag de AI gebruiken?</b> De catalogus staat in code -- daar hoort hij, want een veld
 *       dat niet bestaat kun je niet aanvinken. Hier zet je uit wat nog niet af genoeg is.</li>
 *   <li><b>Wat wacht er om gedeeld te worden?</b> Op je eigen dashboard publiceer je zelf; op dat van
 *       collega's hoort iemand ja te zeggen.</li>
 *   <li><b>Wat missen mensen?</b> Elke keer dat de AI eerlijk nee zegt, staat hier een regel bij. Dat
 *       is een betere roadmapbron dan een wensenlijst, juist omdat niemand er moeite voor hoefde te
 *       doen.</li>
 * </ul>
 */

interface Item {
  schermtype: string;
  soort: string;
  id: string;
  type: string | null;
  recht: string | null;
  aiReady: boolean;
}

interface Verzoek {
  id: string;
  ontwerpId: string;
  ontwerpNaam: string;
  versie: number;
  status: string;
  reden: string | null;
}

interface Gemist {
  schermtype: string;
  verzoek: string;
  reden: string;
  aantal: number;
}

export function SchermbouwerBeheer() {
  const t = useTranslate();
  const { mutateAsync } = useCustomMutation();
  const [schermtype, setSchermtype] = useState("");

  const { data: typesData } = useCustom<string[]>({ url: "schermbouwer/schermtypes", method: "get" });
  const schermtypes = typesData?.data ?? [];
  const gekozen = schermtype || schermtypes[0] || "";

  const { data: itemsData, refetch: herlaadItems } = useCustom<Item[]>({
    url: `schermbouwer/catalogusbeheer?schermtype=${encodeURIComponent(gekozen)}`,
    method: "get",
    queryOptions: { enabled: gekozen !== "" },
  });

  const { data: verzoekenData, refetch: herlaadVerzoeken } = useCustom<Verzoek[]>({
    url: "schermbouwer/publicatieverzoeken",
    method: "get",
  });

  const { data: gemistData } = useCustom<Gemist[]>({ url: "schermbouwer/gemist", method: "get" });

  const items = itemsData?.data ?? [];
  const verzoeken = verzoekenData?.data ?? [];
  const gemist = gemistData?.data ?? [];

  async function zet(item: Item, aiReady: boolean) {
    await mutateAsync({
      url: "schermbouwer/catalogusbeheer",
      method: "put",
      values: { schermtype: item.schermtype, soort: item.soort, itemId: item.id, aiReady },
      successNotification: false,
      errorNotification: false,
    });
    await herlaadItems();
  }

  async function beslis(verzoek: Verzoek, akkoord: boolean) {
    await mutateAsync({
      url: `schermbouwer/publicatieverzoeken/${verzoek.id}/besluit`,
      method: "post",
      values: { akkoord, reden: null },
      successNotification: false,
      errorNotification: false,
    });
    await herlaadVerzoeken();
  }

  return (
    <div className="sb__kolom">
      <p className="sb__waarschuwing">{t("schermbouwer.beheerUitleg")}</p>

      <div className="sb__kaart">
        <span className="sb__kaarttitel">{t("schermbouwer.openVerzoeken")}</span>
        {verzoeken.length === 0 ? (
          <p className="sb__waarschuwing">{t("schermbouwer.geenVerzoeken")}</p>
        ) : (
          <ul className="sb__versies">
            {verzoeken.map((v) => (
              <li key={v.id} className="sb__versie">
                <span className="sb__versie__wat">
                  <b>{v.ontwerpNaam}</b> · v{v.versie}
                  {v.reden ? ` — ${v.reden}` : ""}
                </span>
                <Button variant="primary" onClick={() => void beslis(v, true)}>
                  {t("schermbouwer.goedkeuren")}
                </Button>
                <Button variant="secondary" onClick={() => void beslis(v, false)}>
                  {t("schermbouwer.afwijzen")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="sb__kaart">
        <div className="sb__voorbeeldbalk">
          <span className="sb__kaarttitel">{t("schermbouwer.aiReady")}</span>
          <select value={gekozen} onChange={(e) => setSchermtype(e.target.value)}>
            {schermtypes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <ul className="sb__versies">
          {items.map((item) => (
            <li key={`${item.soort}/${item.id}`} className="sb__versie">
              <label className="sb__schakelaar">
                <input
                  type="checkbox"
                  checked={item.aiReady}
                  onChange={(e) => void zet(item, e.target.checked)}
                />
                <span className="sb__versie__wat">
                  <b>{item.id}</b>
                  <span className="sb__waarschuwing">
                    {" "}
                    {item.soort}
                    {item.type ? ` · ${item.type}` : ""}
                    {item.recht ? ` · ${item.recht}` : ""}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>

      <div className="sb__kaart">
        <span className="sb__kaarttitel">{t("schermbouwer.gemisteMogelijkheden")}</span>
        {gemist.length === 0 ? (
          <p className="sb__waarschuwing">{t("schermbouwer.geenGemist")}</p>
        ) : (
          <ul className="sb__versies">
            {gemist.map((g, i) => (
              <li key={i} className="sb__versie">
                <span className="sb__versie__nr">{t("schermbouwer.aantalKeer", { args: [g.aantal] })}</span>
                <span className="sb__versie__wat">
                  {g.verzoek}
                  <span className="sb__waarschuwing"> · {g.schermtype}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
